import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { DISCOUNT_AMOUNT_DECIMALS, type DiscountType } from '../discount-type/discount-type';
import {
  STACKING_STRICTNESS,
  StackingPolicySchema,
  type StackingPolicy,
} from '../stacking-policy/stacking-policy';

/** The part of a grant that decides what it is worth on an order. */
export interface ApplicableGrant {
  id: string;
  discountType: DiscountType;
  value: number;
  stackingPolicy: StackingPolicy;
  /** Empty means every product (matrix row 19). */
  appliesToProductKeys: readonly string[];
}

/**
 * What a member's grants are worth against one product at one price — the
 * result of `CoursesGetActiveDiscountQuery`, and the **only** thing billing
 * needs to know about courses (N6).
 *
 * Billing receives an answer, not a policy: it never re-derives stacking, never
 * compares grants, and never learns why one was dropped. That is what stops the
 * two modules disagreeing about a price the member has already been shown.
 */
/**
 * What one grant took off the order — the figure its redemption record carries.
 *
 * ## Why the split is computed HERE and not by the caller
 *
 * `RedemptionRequest` wants a **per-grant** amount, and billing has only the
 * combined one. It could not compute the split even if it wanted to: a grant's
 * type and value never cross the seam (N6), and by the time billing holds the
 * outcome it knows nothing but ids. Any allocation it invented would therefore
 * be a **second copy of the stacking policy**, living in the module this
 * contract exists to keep policy out of.
 *
 * So the module that already computed each grant's contribution publishes it,
 * and billing passes each part straight through to `redeem`.
 *
 * **The parts sum to `amountOff` exactly.** They are allocated in proportion to
 * what each grant would have been worth alone, and the **last part takes the
 * rounding remainder** — so no combination of thirds can record a total that
 * differs from what the member was actually given. That matters because the
 * redemption rows are what a refund and an audit are read from: parts that sum
 * to a penny more than the discount is a discrepancy nobody can explain later.
 */
export const DiscountBreakdownPartSchema = z
  .object({
    grantId: UuidSchema,
    amountOff: z.number(),
  })
  .strict();

export type DiscountBreakdownPart = z.infer<typeof DiscountBreakdownPartSchema>;

export const DiscountOutcomeSchema = z
  .object({
    /** The policy that governed — the strictest among the applicable grants. */
    policy: StackingPolicySchema,
    /** Exactly the grants that make up this outcome, and therefore the ones to redeem. */
    grantIds: z.array(UuidSchema),
    /**
     * The same grants, each with what it took off — one entry per `grantIds`
     * member, in the same order. See {@link DiscountBreakdownPartSchema}.
     */
    breakdown: z.array(DiscountBreakdownPartSchema),
    /** The combined percentage, clamped to 100. */
    percentOff: z.number(),
    /** Money off, after the percentage — `price - finalPrice`. */
    amountOff: z.number(),
    finalPrice: z.number(),
  })
  .strict();

export type DiscountOutcome = z.infer<typeof DiscountOutcomeSchema>;

export interface ResolveDiscountOutcomeInput {
  grants: readonly ApplicableGrant[];
  productKey: string;
  /** The undiscounted price. It is what makes a percent and a fixed comparable. */
  price: number;
}

const MONEY_STEP = 10 ** DISCOUNT_AMOUNT_DECIMALS;

/** Two-decimal money, without the float noise `0.1 + 0.2` leaves behind. */
function round(value: number): number {
  return Math.round(value * MONEY_STEP) / MONEY_STEP;
}

function appliesTo(grant: ApplicableGrant, productKey: string): boolean {
  return grant.appliesToProductKeys.length === 0 || grant.appliesToProductKeys.includes(productKey);
}

/** What one grant alone would take off this price — the comparable for `best_of`. */
function worth(grant: ApplicableGrant, price: number): number {
  return grant.discountType === 'percent'
    ? round((price * grant.value) / 100)
    : Math.min(grant.value, price);
}

/**
 * Resolves matrix rows 11–13 and 19 in one place.
 *
 * ## Why this is in `contracts` rather than in the api
 *
 * The same reason `hasPermission` is: more than one surface must reach the same
 * answer. The api's responder is the authority billing reads (N14 — gating is
 * server-side), and P22b's rule editor wants to *preview* what a member with
 * these grants would pay. A second implementation for the preview would be a
 * second place the policy lives, and it would drift.
 *
 * ## The order of operations is percent, then fixed
 *
 * Stated rather than discovered, because the two orders give different money.
 * Percent-first is the conventional one and the one that favours the platform's
 * floor: a fixed amount applied to an already-reduced price cannot be leveraged
 * by a percentage on top of it. `finalPrice` is the authority; `amountOff` is
 * derived from it, so the three numbers can never disagree.
 *
 * ## Ties are broken by id
 *
 * Two equal-value grants under `best_of` must resolve identically on every read,
 * or the member sees one discount on the paywall and a different grant is
 * redeemed at checkout. Insertion order is not a guarantee any query gives.
 */
export function resolveDiscountOutcome(input: ResolveDiscountOutcomeInput): DiscountOutcome {
  const { productKey, price } = input;

  const applicable = input.grants.filter((grant) => appliesTo(grant, productKey));

  if (applicable.length === 0) {
    return {
      policy: 'stack',
      grantIds: [],
      breakdown: [],
      percentOff: 0,
      amountOff: 0,
      finalPrice: round(price),
    };
  }

  // The strictest policy present governs the whole order — a member must not be
  // able to spend a `none` grant alongside anything by ordering their basket
  // differently.
  const policy = applicable.reduce<StackingPolicy>(
    (strictest, grant) =>
      STACKING_STRICTNESS[grant.stackingPolicy] > STACKING_STRICTNESS[strictest]
        ? grant.stackingPolicy
        : strictest,
    'stack',
  );

  const chosen = policy === 'stack' ? applicable : [pickBest(applicable, price)];

  let percentOff = 0;
  let fixedOff = 0;

  for (const grant of chosen) {
    if (grant.discountType === 'percent') {
      percentOff += grant.value;
    } else {
      fixedOff += grant.value;
    }
  }

  percentOff = Math.min(percentOff, 100);

  const afterPercent = round(price * (1 - percentOff / 100));
  const finalPrice = round(Math.max(0, afterPercent - fixedOff));
  const amountOff = round(price - finalPrice);

  return {
    policy,
    grantIds: chosen.map((grant) => grant.id),
    breakdown: allocate(chosen, price, amountOff),
    percentOff,
    amountOff,
    finalPrice,
  };
}

/**
 * Splits what was actually given across the grants that gave it.
 *
 * Proportional to each grant's standalone {@link worth}, which is the only
 * comparable a percent and a fixed amount share — and the same comparable
 * `best_of` already ranks by, so one notion of "what this grant is worth"
 * serves both.
 *
 * Two properties the caller depends on, both load-bearing:
 *
 * - **The parts sum to `amountOff` exactly.** Each share is capped at what is
 *   still unassigned and the **last** part takes the remainder, so rounding
 *   cannot make the total drift in either direction. Distributing `round(share)`
 *   to every part and hoping is what produces a redemption trail a penny away
 *   from the order it belongs to.
 * - **No part is negative.** The cap keeps `assigned` at or below `amountOff`,
 *   so the remainder handed to the last part is never below zero.
 *
 * `total <= 0` is reachable — a zero price, or a zero-valued grant — and there
 * is nothing to apportion then: `amountOff` is zero too, so every part is zero
 * and the invariant still holds.
 */
function allocate(
  chosen: readonly ApplicableGrant[],
  price: number,
  amountOff: number,
): DiscountBreakdownPart[] {
  const parts = chosen.map((grant) => ({ id: grant.id, worth: worth(grant, price) }));
  const total = parts.reduce((sum, part) => sum + part.worth, 0);

  let assigned = 0;

  return parts.map((part, index) => {
    const remaining = round(amountOff - assigned);

    const share =
      index === parts.length - 1
        ? remaining
        : Math.min(remaining, total > 0 ? round((amountOff * part.worth) / total) : 0);

    assigned = round(assigned + share);

    return { grantId: part.id, amountOff: share };
  });
}

function pickBest(grants: readonly ApplicableGrant[], price: number): ApplicableGrant {
  return grants.reduce((best, grant) => {
    const difference = worth(grant, price) - worth(best, price);

    if (difference > 0) {
      return grant;
    }

    return difference === 0 && grant.id < best.id ? grant : best;
  });
}
